/* eslint-disable react-hooks/exhaustive-deps */
/* PACKAGES */
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

/* COMPONENTS */
import Topbar from "../../../PulseSurvey/Topbar";
import Loader from "../../../../component/common/Loader";

/* ICONS */
import { RiLink } from "react-icons/ri";

/* COMPANY API */
import { CompanyApi } from "../../../../api/companyApi";

const WellbankInvoiceDetails = () => {
  /* LOCATION */
  const location = useLocation();

  /* INVOICE ID */
  const invoiceId = location.pathname.split("/")[4];

  /* INVOICE DETAILS */
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  /* FETCH INVOICE DETAILS */
  const fetchInvoiceDetails = async () => {
    setLoading(true);
    CompanyApi.getNewInvoiceDetails(invoiceId)
      .then((res) => {
        setInvoiceData(res?.data?.data);
      })
      .catch((err) => {
        console.log("Error: ", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* USE EFFECT */
  useEffect(() => {
    fetchInvoiceDetails();
  }, [invoiceId]);

  return (
    <div className="relative pl-5">
      {loading && <Loader />}

      {/* HELMET */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>Invoice Details | Shoorah Admin</title>
      </Helmet>

      {/* TOPBAR */}
      <Topbar
        title="Invoice details"
        hasDesc
        description="You have received an invoice from Shoorah due to your request. Please review the details below."
      />

      {/* DETAILS */}
      <div className="mt-10">
        {/* FIRST SECTION */}
        <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg">
          <div className="p-5">
            <div className="grid grid-cols-2 gap-14">
              {/* LEFT SECTION */}
              <div className="col-span-1">
                {/* COMPANY DETAILS */}
                <div className="mt-0">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Request details
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      These are the details for the invoice request.
                    </p>
                  </div>

                  <div className="mt-5">
                    {/* WELLPOINTS TOTAL */}
                    <div className="mt-0">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Wellpoints total
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="totalRequested"
                          value={invoiceData?.well_points || "-"}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* AMOUNT OWED */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Amount owed
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="amount"
                          value={invoiceData?.wellpoints_amount || "-"}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* INVOICE DETAILS */}
                <div className="mt-10">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Invoice details
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      These are the details the invoice has been set up with.
                    </p>
                  </div>

                  <div className="mt-5">
                    {/* NAME */}
                    <div className="mt-0">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Name
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="name"
                          value={invoiceData?.invoiceDetail?.name || "-"}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* EMAIL */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Email
                      </label>
                      <div className="mt-1">
                        <input
                          type="email"
                          disabled
                          name="email"
                          value={invoiceData?.invoiceDetail?.email || "-"}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* ADDRESS */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Address
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="address"
                          value={invoiceData?.invoiceDetail?.address || "-"}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT SECTION */}
              <div className="col-span-1">
                {/* ATTACHED INVOICE/PREVIEW */}
                <div className="mt-0">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Preview invoice from Shoorah
                    </h5>
                  </div>

                  {/* FILE BOX */}
                  <div className="mt-5 bg-[#F4F4F4] dark:bg-shoorah-darkBgColor rounded-lg p-5">
                    <p className="text-sm font-normal text-black dark:text-white">
                      Shoorah has attached this invoice. Please ensure payment
                      is made to receive wellpoints.
                    </p>

                    {/* FILE */}
                    <div className="mt-5">
                      <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg py-3 px-4">
                        <div className="flex items-center gap-4">
                          <RiLink className="w-5 h-5 text-black dark:text-white flex-shrink-0" />
                          <div className="p-0">
                            <h5 className="text-sm font-medium text-black dark:text-white">
                              {invoiceData?.invoice?.split("/")[5] || "-"}
                            </h5>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* OPTIONS */}
                    <div className="mt-5 flex items-center gap-2">
                      <a
                        href={invoiceData?.invoice}
                        rel="noreferrer"
                        target="_blank"
                        className="text-sm font-normal text-black dark:text-white hover:text-shoorah-primary transition-all duration-300 ease-in-out"
                      >
                        Download file
                      </a>
                    </div>
                  </div>
                </div>

                {/* ADDITIONAL DETAILS */}
                <div className="mt-10">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Additional details
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      You can add additional details. Please note, these will
                      also be visible to the company.
                    </p>
                  </div>

                  <div className="mt-5">
                    {/* INVOICE NUMBER */}
                    <div className="mt-0">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Invoice number
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="invoiceNumber"
                          value={invoiceData?.invoice_number || "-"}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* AMOUNT */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Amount (£)
                      </label>
                      <div className="mt-1">
                        <input
                          type="number"
                          disabled
                          name="amount"
                          value={invoiceData?.invoice_amount || "-"}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECOND SECTION */}
        <div className="mt-5">
          <div className="grid grid-cols-2 gap-5">
            {/* INTERNAL STATUS */}
            <div className="col-span-1">
              <div className="h-full bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg">
                <div className="p-5">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Internal status
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      The status will update once Shoorah has processed the
                      payment.
                    </p>
                  </div>

                  {/* CONTENT */}
                  <div className="mt-5">
                    {invoiceData?.status === 2 && (
                      <div className="w-full bg-yellow-400 bg-opacity-10 px-4 py-3 border border-yellow-500 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-yellow-600">
                            Unpaid
                          </p>
                        </div>
                      </div>
                    )}

                    {invoiceData?.status === 1 && (
                      <div className="w-full bg-green-400 bg-opacity-10 px-4 py-3 border border-green-500 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-green-600">
                            Paid
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellbankInvoiceDetails;
